import { cn } from '../../lib/utils';

export type KpiCell = {
	label: string;
	value: React.ReactNode;
	/** One context line; colour only the part that needs attention. */
	sub?: React.ReactNode;
	/** Colour the number itself (e.g. red when something is overdue). */
	valueTone?: 'danger';
	onClick?: () => void;
};

/**
 * One card split into equal cells: uppercase label, a large number, one line of context. The
 * dashboard's key-number pattern — no decorative charts, the line underneath carries the meaning.
 */
export function KpiStrip({ cells, loading }: { cells: KpiCell[]; loading?: boolean }) {
	return (
		<section
			aria-label="Key numbers"
			className="grid grid-cols-2 overflow-hidden rounded-xl border border-line-1 bg-surface lg:grid-cols-4"
		>
			{cells.map((cell, i) => {
				const Tag = cell.onClick ? 'button' : 'div';
				return (
					<Tag
						key={cell.label}
						type={cell.onClick ? 'button' : undefined}
						onClick={cell.onClick}
						className={cn(
							'flex flex-col gap-2 border-line-1 px-[18px] pt-4 pb-[18px] text-left transition-colors',
							i % 2 === 1 && 'border-l',
							i >= 2 && 'border-t lg:border-t-0',
							i === 2 && 'lg:border-l',
							cell.onClick && 'cursor-pointer hover:bg-surface-hover'
						)}
					>
						<span className="text-2xs font-medium uppercase tracking-[0.12em] text-text-4">
							{cell.label}
						</span>
						{loading ? (
							<span className="h-7 w-12 animate-pulse rounded bg-surface-fill" />
						) : (
							<span
								className={cn(
									'font-sans text-[28px] font-normal leading-none -tracking-[0.03em] tabular-nums',
									cell.valueTone === 'danger'
										? 'text-status-danger'
										: 'text-text-1'
								)}
							>
								{cell.value}
							</span>
						)}
						{cell.sub && (
							<span className="text-xs tabular-nums text-text-3">{cell.sub}</span>
						)}
					</Tag>
				);
			})}
		</section>
	);
}
